<template>
  <div class="ol-actions-panel ol-actions-replan" role="tabpanel">
    <div class="ol-actions-banner ol-actions-banner--warning">
      <i class="icon-info ol-actions-banner__icon" aria-hidden="true"></i>
      <p class="ol-actions-banner__text mb-0">
        {{ $t("lang.calendar.reschedule_replanifier") }}
      </p>
    </div>

    <Info
      v-if="isExpired"
      :message="$t('lang.appointments.expired')"
      :color="'danger'"
    />

    <template v-if="!isExpired">
      <div
        v-if="event && event.event_date"
        class="ol-actions-appointment-card"
      >
        <i class="icon-calendar" aria-hidden="true"></i>
        <span class="ol-actions-appointment-card__text">
          <span class="ol-actions-appointment-card__label">{{
            $t("lang.opportunities.current_appointment")
          }}</span>
          {{ formattedCurrentEventDate }}
        </span>
        <span
          v-if="isEventDatePassed"
          class="ol-actions-appointment-card__badge"
        >
          {{ $t("lang.opportunities.date_passed_badge") }}
        </span>
      </div>

      <form class="ol-actions-schedule-form" @submit.prevent>
        <div class="ol-actions-schedule-form__row">
          <div class="ol-actions-schedule-form__field">
            <label class="ol-actions-schedule-form__label">{{
              $t("lang.opportunities.new_date")
            }}</label>
            <div class="ol-actions-schedule-form__picker-wrap">
              <date-picker
                v-model="day"
                :config="configs.day"
                class="ol-actions-schedule-form__picker"
              />
            </div>
          </div>
          <div class="ol-actions-schedule-form__field">
            <label class="ol-actions-schedule-form__label">{{
              $t("lang.opportunities.new_time")
            }}</label>
            <div class="ol-actions-schedule-form__picker-wrap">
              <date-picker
                v-model="hour"
                :config="configs.hour"
                class="ol-actions-schedule-form__picker"
              />
            </div>
          </div>
        </div>

        <div class="ol-actions-schedule-form__field ol-actions-schedule-form__field--full">
          <label class="ol-actions-schedule-form__label">
            {{ $t("lang.opportunities.comment_optional") }}
          </label>
          <textarea
            v-model="remark"
            class="ol-actions-schedule-form__textarea"
            :placeholder="$t('lang.opportunities.replan_comment_placeholder')"
            rows="3"
          />
        </div>
      </form>

      <div class="ol-actions-panel__footer ol-actions-panel__footer--end ol-actions-panel__footer--gap">
        <button
          type="button"
          class="ol-actions-btn ol-actions-btn--outline"
          @click="resetForm"
        >
          {{ $t("lang.button.cancel") }}
        </button>
        <button
          type="button"
          class="ol-actions-btn ol-actions-btn--primary"
          :disabled="!canSave || loadingCopyEvent"
          @click="copyEvent"
        >
          {{ $t("lang.opportunities.confirm_replanify") }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { linksService } from "@/_services";
import { mapActions } from "vuex";
import moment from "moment";
import Info from "@/components/Common/Info.vue";

export default {
  components: { Info },
  data() {
    return {
      eventId: "1",
      day: moment().add(1, "day").format("DD-MM-YYYY"),
      hour: "9:00 AM",
      remark: "",
      configs: {
        day: {
          format: "DD-MM-YYYY",
          useCurrent: false,
          minDate: moment().endOf("day"),
        },
        hour: {
          format: "LT",
          useCurrent: false,
        },
      },
      loadingCopyEvent: false,
    };
  },

  props: {
    linkId: String,
    event: Object,
  },

  computed: {
    isExpired() {
      return this.esFechaPasada() > 1;
    },

    isEventDatePassed() {
      return this.esFechaPasada() > 0;
    },

    formattedCurrentEventDate() {
      if (!this.event || !this.event.event_date) {
        return "";
      }
      const parsed = moment(this.event.event_date);
      if (!parsed.isValid()) {
        return this.event.event_date;
      }
      return parsed.format("D MMMM YYYY [à] HH:mm");
    },

    canSave() {
      return this.remark != "" && this.day != null && this.hour != null;
    },
  },

  methods: {
    ...mapActions("opportunities", {
      reloadOpportunity: "reloadOpportunity",
    }),

    esFechaPasada() {
      let fecha = this.event.event_date;
      var fechaActual = new Date();
      var fechaProporcionada = new Date(fecha);
      var diferencia = fechaActual - fechaProporcionada;
      var horasDiferencia = diferencia / (1000 * 60 * 60);
      return horasDiferencia / 24;
    },

    resetForm() {
      this.day = moment().add(1, "day").format("DD-MM-YYYY");
      this.hour = "9:00 AM";
      this.remark = "";
    },

    copyEvent() {
      this.$swal({
        type: "warning",
        title: this.$t("lang.label.areyousure"),
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33a2c",
        confirmButtonText: this.$t("lang.button.yes"),
        cancelButtonText: this.$t("lang.button.cancel"),
      }).then((result) => {
        if (result.value) {
          this.loadingCopyEvent = true;
          const eventDate = this.createEventDate(
            this.day,
            this.formatTime(this.hour)
          );
          let params = {
            remark: this.remark,
            event_date: eventDate,
          };
          linksService
            .copyEvent(this.linkId, params)
            .then(() => {
              this.reloadOpportunity({ headerTab: "NOTHING" });
              this.$swal({
                type: "success",
                title: this.$t("lang.calendar.success_reschedule"),
                showConfirmButton: false,
                timer: 2500,
              });
            })
            .finally(() => {
              this.loadingCopyEvent = false;
            });
        }
      });
    },

    createEventDate(day, hour) {
      var dayArray = day.split(/-|\s|:/);
      return `${dayArray[2]}-${dayArray[1]}-${dayArray[0]} ${hour}`;
    },

    formatTime(time) {
      var hours = Number(time.match(/^(\d+)/)[1]);
      var minutes = Number(time.match(/:(\d+)/)[1]);
      var AMPM = time.match(/\s(.*)$/)[1];
      if (AMPM == "PM" && hours < 12) hours = hours + 12;
      if (AMPM == "AM" && hours == 12) hours = hours - 12;
      var sHours = hours.toString();
      var sMinutes = minutes.toString();
      if (hours < 10) sHours = "0" + sHours;
      if (minutes < 10) sMinutes = "0" + sMinutes;
      return `${sHours}:${sMinutes}:00`;
    },
  },
};
</script>

<style scoped>
.ol-actions-panel {
  padding: 1rem 1.25rem;
}

.ol-actions-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 14px;
}

.ol-actions-banner--warning {
  background: #fef3c7;
  border: 1px solid #fac775;
}

.ol-actions-banner--warning .ol-actions-banner__icon {
  color: #92400e;
  font-size: 16px;
  margin-top: 1px;
  flex-shrink: 0;
}

.ol-actions-banner--warning .ol-actions-banner__text {
  font-size: 12px;
  color: #854f0b;
  line-height: 1.5;
}

.ol-actions-appointment-card {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fafafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
  margin: 14px 0;
  flex-wrap: wrap;
}

.ol-actions-appointment-card__text {
  flex: 1;
  font-size: 12px;
  color: #374151;
  min-width: 0;
}

.ol-actions-appointment-card__label {
  color: #6b7280;
}

.ol-actions-appointment-card__badge {
  background: #fee2e2;
  color: #991b1b;
  padding: 2px 8px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 500;
  flex-shrink: 0;
}

.ol-actions-schedule-form__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 14px;
}

.ol-actions-schedule-form__label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.ol-actions-schedule-form__label-hint {
  color: #6b7280;
  font-weight: 400;
}

.ol-actions-schedule-form__picker-wrap {
  position: relative;
}

.ol-actions-schedule-form__picker >>> .input-group {
  width: 100%;
}

.ol-actions-schedule-form__picker >>> .input-group-prepend,
.ol-actions-schedule-form__picker >>> .input-group-append {
  display: none;
}

.ol-actions-schedule-form__picker >>> input {
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: #111827;
  background: white;
}

.ol-actions-schedule-form__textarea {
  width: 100%;
  min-height: 70px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  color: #111827;
  resize: vertical;
  line-height: 1.5;
  outline: none;
  box-sizing: border-box;
}

.ol-actions-panel__footer {
  display: flex;
  align-items: center;
  margin-top: 14px;
}

.ol-actions-panel__footer--end {
  justify-content: flex-end;
}

.ol-actions-panel__footer--gap {
  gap: 8px;
}

.ol-actions-btn {
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 14px;
  line-height: 1.2;
}

.ol-actions-btn--primary {
  background: #4b4be1;
  color: white;
  border: none;
}

.ol-actions-btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ol-actions-btn--outline {
  background: white;
  color: #6b7280;
  border: 1px solid #e5e7eb;
}

@media (max-width: 576px) {
  .ol-actions-schedule-form__row {
    grid-template-columns: 1fr;
  }
}
</style>
